import { DateTime } from 'luxon'
import ScheduledPeriodTranslation from 'src/models/rental/workflow/ScheduledPeriodTranslation'
import DurationPeriod from 'src/models/period/DurationPeriod'
import ScheduledPeriodJson from 'src/models/rental/workflow/interfaces/ScheduledPeriodJson'
import { Locales } from 'src/i18n'

export default class ScheduledPeriod {
  reference: string
  translations: { [locale: string]: ScheduledPeriodTranslation }
  visibleFrom: DateTime | null
  visibleUntil: DateTime | null
  activePeriods: Array<DurationPeriod>

  constructor(
    reference: string,
    translations: { [locale: string]: ScheduledPeriodTranslation },
    visibleFrom: DateTime | null,
    visibleUntil: DateTime | null,
    activePeriods: Array<DurationPeriod>,
  ) {
    this.reference = reference
    this.translations = translations
    this.visibleFrom = visibleFrom
    this.visibleUntil = visibleUntil
    this.activePeriods = activePeriods
  }

  findTranslationByLocale(locale: Locales): ScheduledPeriodTranslation | null {
    return this.translations[locale] ?? null
  }

  translate(locale: Locales, fallbackLocale: Locales) {
    let translation = this.findTranslationByLocale(locale)

    if (!translation) {
      translation = this.findTranslationByLocale(fallbackLocale)
    }

    return translation
  }

  isActive(now: DateTime): boolean {
    if (this.visibleFrom && this.visibleFrom > now) {
      return false
    }
    if (this.visibleUntil && this.visibleUntil < now) {
      return false
    }

    for (const activePeriod of this.activePeriods) {
      if (activePeriod.startDate <= now && activePeriod.endDate > now) {
        return true
      }
    }

    return false
  }

  static fromJson(data: ScheduledPeriodJson) {
    return new this(
      data.reference,
      data.translations.reduce(
        (previous, current) => ({
          ...previous,
          [current.locale]: ScheduledPeriodTranslation.fromJson(current),
        }),
        {},
      ),
      data.visibleFrom
        ? DateTime.fromISO(data.visibleFrom.date, {
            zone: data.visibleFrom.timezone,
          })
        : null,
      data.visibleUntil
        ? DateTime.fromISO(data.visibleUntil.date, {
            zone: data.visibleUntil.timezone,
          })
        : null,
      data.activePeriods.map((period) => DurationPeriod.fromJson(period)),
    )
  }
}
